<template>
  <div
    :class="{
      'accordion w-full': true,
    }"
  >
    <PortableText
      v-if="props.heading?.standardPortableText"
      :class="{
        '-mx-3 mb-70px text-www-color-neutral-medium-dark md:mb-30px': true,
      }"
      :portable-data="props.heading?.standardPortableText"
    />
    <AccordionItem
      v-for="(item, index) in props.questions"
      :key="item._key ?? index"
      v-model="accordionOpenIndex"
      v-bind="item"
      :is-open="accordionOpenIndex === index"
      :index="index"
      :id-base="item._key ?? `faq-item-${index}`"
      :class="{
        'mx-auto my-10px': true,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import AccordionItem from './AccordionItem.vue';
import PortableText from '~/components/Atoms/PortableText/PortableText.vue';
import type { FAQAccordion } from '~/sanity/types';
import { serializeJsonLd, extractPortableText } from '~/utils/json-ld-utils';
const props = defineProps<FAQAccordion>();
const accordionOpenIndex = ref<number>();

const route = useRoute();

// FAQPage JSON-LD is intentionally owned at the accordion component level.
// Do not add FAQPage through usePageHead/structuredDataBuilder to avoid duplicate schemas.
const baseUrl = process.env.NUXT_BASE_URL || 'https://www.onshape.com';

// Include a stable per-instance suffix so multiple FAQAccordion components on the
// same route do not emit duplicate FAQPage @id values (which can happen with section
// builders / portable text composition).
const faqInstanceId = computed(() => {
  const instanceKey = (props as FAQAccordion & { _key?: string })._key?.trim();
  if (instanceKey) {
    return instanceKey;
  }

  const questionSignature = props.questions
    ?.map((questionItem, index) => questionItem?._key?.trim() || `q${index}`)
    .join('-');

  if (questionSignature) {
    return questionSignature;
  }

  return 'default';
});

const mainEntity = computed(() => {
  if (!props.questions?.length) {
    return [];
  }

  return props.questions
    .map((questionItem) => {
      const questionName = questionItem?.question?.trim();
      const answerText = extractPortableText(questionItem?.answer?.insideLayoutPortableText);

      if (!questionName || !answerText) {
        return null;
      }

      return {
        '@type': 'Question',
        name: questionName,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText,
        },
      };
    })
    .filter(
      (
        entry
      ): entry is {
        '@type': 'Question';
        name: string;
        acceptedAnswer: { '@type': 'Answer'; text: string };
      } => Boolean(entry)
    );
});

const faqJsonLd = computed(() => {
  if (!mainEntity.value.length) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${new URL(route.path, baseUrl).toString()}#faq-${faqInstanceId.value}`,
    mainEntity: mainEntity.value,
  };
});

useHead(() => {
  if (!faqJsonLd.value) {
    return {};
  }

  return {
    script: [
      {
        key: `structured-data-faq-${faqInstanceId.value}`,
        type: 'application/ld+json',
        innerHTML: serializeJsonLd(faqJsonLd.value),
      },
    ],
  };
});
</script>

<style>
/* Customize Accordion Protable text Styles  */
.accordion-body .text-one-column ul {
  margin-top: 1.4rem;
  list-style: disc;
  margin-left: 2.5rem;
}

.accordion-body .text-one-column ul li {
  margin-bottom: 0.5rem;
}
.accordion-body .text-one-column ul li:last-child {
  margin-bottom: 0;
}
</style>
